import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DeviceMobileIcon,
  DesktopIcon,
  PaperPlaneTiltIcon,
  PlusIcon,
  TrashIcon,
  WebhooksLogoIcon,
} from '@phosphor-icons/react';
import { CHANNEL_KINDS, type Channel, type ChannelKind } from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { CHANNEL_LABEL, DELIVERY_MODE_LABEL, relativeTime } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import { Dialog } from '../components/Dialog.tsx';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Select,
  cx,
} from '../components/ui.tsx';

const KIND_ICON: Record<ChannelKind, typeof DeviceMobileIcon> = {
  apns_ios: DeviceMobileIcon,
  apns_macos: DesktopIcon,
  webhook: WebhooksLogoIcon,
};

/**
 * A channel is one place an alert can land: a phone, a Mac, another system.
 * The page exists so nobody has to find out during an incident that a device
 * token expired six weeks ago, hence the visible last-success and test button.
 */
export function ChannelsPage() {
  const { user, can } = useSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; detail: string } | null>(null);

  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api.channels.list({ limit: 100 }) });

  const test = useMutation({
    mutationFn: (id: string) => api.channels.test(id),
    onSuccess: (result, id) => {
      setTestResult({ id, ok: result.delivered, detail: result.detail });
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.channels.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });

  return (
    <>
      <PageHeader
        title="Kanäle"
        description="Geräte und Endpunkte, an die Alarme zugestellt werden. Ein Test schickt eine echte Benachrichtigung über denselben Weg wie ein echter Alarm."
        action={
          <Button variant="primary" icon={<PlusIcon size={15} />} onClick={() => setCreating(true)}>
            Kanal hinzufügen
          </Button>
        }
      />

      <Panel bodyClassName="p-0">
        {channels.isLoading ? (
          <ListSkeleton rows={3} />
        ) : channels.isError ? (
          <ErrorState message="Die Kanäle konnten nicht geladen werden." onRetry={() => channels.refetch()} />
        ) : channels.data && channels.data.items.length > 0 ? (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {channels.data.items.map((channel) => {
              const Icon = KIND_ICON[channel.kind];
              const result = testResult?.id === channel.id ? testResult : null;
              const mine = channel.user_id === user?.id;

              return (
                <li key={channel.id} className="px-4 py-3.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={cx(
                        'flex size-9 shrink-0 items-center justify-center rounded-md border',
                        channel.is_active
                          ? 'border-line-strong bg-sunken text-ink-2'
                          : 'border-line bg-sunken text-ink-3',
                      )}
                    >
                      <Icon size={17} />
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-[13px] font-medium text-ink">
                        {channel.label}
                        {!channel.is_active ? (
                          <span className="rounded-sm bg-sunken px-1.5 text-[11px] font-normal text-ink-3">
                            deaktiviert
                          </span>
                        ) : null}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[11.5px] text-ink-3">
                        {channel.config_preview}
                      </p>
                    </div>

                    <div className="hidden w-48 shrink-0 text-[12px] text-ink-3 lg:block">
                      <p>{CHANNEL_LABEL[channel.kind]} - {channel.user_name}</p>
                      <p>
                        {channel.last_success_at
                          ? `zuletzt ${relativeTime(channel.last_success_at)}`
                          : 'noch nie zugestellt'}
                      </p>
                      {channel.focus?.active && channel.focus.mode_cap ? (
                        <p className="mt-0.5 text-warning">
                          {channel.focus.focus_name ?? 'Focus'} aktiv:{' '}
                          {DELIVERY_MODE_LABEL[channel.focus.mode_cap]}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 gap-1.5">
                      <Button
                        size="sm"
                        icon={<PaperPlaneTiltIcon size={14} />}
                        disabled={test.isPending}
                        onClick={() => test.mutate(channel.id)}
                      >
                        Test
                      </Button>
                      {(mine || can('admin')) ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`${channel.label} löschen`}
                          onClick={() => {
                            if (confirm(`Kanal "${channel.label}" wirklich löschen?`)) {
                              remove.mutate(channel.id);
                            }
                          }}
                        >
                          <TrashIcon size={14} />
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {result ? (
                    <p
                      role="status"
                      className={cx(
                        'mt-2.5 rounded-md border px-3 py-2 text-[12px] leading-relaxed',
                        result.ok
                          ? 'border-accent/40 bg-accent-soft text-ink'
                          : 'border-critical/40 bg-critical-soft text-critical',
                      )}
                    >
                      {result.detail}
                    </p>
                  ) : null}

                  {!result && channel.last_error ? (
                    <p className="mt-2 font-mono text-[11.5px] leading-relaxed text-critical">
                      Letzter Fehler: {channel.last_error}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState
            icon={<DeviceMobileIcon size={26} weight="light" />}
            title="Noch kein Kanal"
            description="Ohne Kanal hat eine Weiterleitungsregel kein Ziel. Für Push brauchst du das Device-Token aus der App, für einen Webhook nur eine URL."
            action={
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                Ersten Kanal hinzufügen
              </Button>
            }
          />
        )}
      </Panel>

      {creating ? (
        <CreateChannelDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['channels'] });
          }}
        />
      ) : null}
    </>
  );
}

function CreateChannelDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}) {
  const { user, can } = useSession();
  const [kind, setKind] = useState<ChannelKind>('apns_ios');
  const [label, setLabel] = useState('');
  const [userId, setUserId] = useState(user?.id ?? '');
  const [deviceToken, setDeviceToken] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [signed, setSigned] = useState(false);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list({ limit: 100 }),
    enabled: can('admin'),
  });

  const create = useMutation({
    mutationFn: () => {
      if (kind === 'webhook') {
        return api.channels.create({
          kind: 'webhook',
          label,
          user_id: userId,
          config: { url, secret: signed && secret ? secret : null },
        });
      }
      return api.channels.create({
        kind,
        label,
        user_id: userId,
        config: { device_token: deviceToken.trim(), bundle_id: bundleId || null },
      });
    },
    onSuccess: onCreated,
  });

  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors() : {};
  const generalError =
    create.error instanceof ApiError && Object.keys(fieldErrors).length === 0
      ? create.error.message
      : null;

  return (
    <Dialog title="Kanal hinzufügen" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Art">
          {(id) => (
            <Select id={id} value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>
              {CHANNEL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {CHANNEL_LABEL[k]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Bezeichnung" hint="Wie du dieses Gerät nennst." error={fieldErrors.label}>
          {(id) => (
            <Input
              id={id}
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={kind === 'webhook' ? 'Chat-Brücke' : 'Diensthandy'}
            />
          )}
        </Field>

        {can('admin') ? (
          <Field label="Gehört zu">
            {(id) => (
              <Select id={id} value={userId} onChange={(e) => setUserId(e.target.value)}>
                {(users.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {kind === 'webhook' ? (
          <>
            <Field label="URL" error={fieldErrors['config.url']}>
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://empfänger.example/hooks/meshalert"
                />
              )}
            </Field>
            <Checkbox
              label="Anfragen signieren"
              description="Setzt einen HMAC im Header X-MeshAlert-Signature."
              checked={signed}
              onChange={(e) => setSigned(e.target.checked)}
            />
            {signed ? (
              <Field label="Signaturgeheimnis" error={fieldErrors['config.secret']}>
                {(id) => (
                  <Input
                    id={id}
                    required
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    className="font-mono"
                  />
                )}
              </Field>
            ) : null}
          </>
        ) : (
          <>
            <Field
              label="Device-Token"
              hint="Hex-Token, das die App bei der Registrierung von Apple bekommt."
              error={fieldErrors['config.device_token']}
            >
              {(id) => (
                <Input
                  id={id}
                  required
                  value={deviceToken}
                  onChange={(e) => setDeviceToken(e.target.value)}
                  placeholder="a1b2c3..."
                  className="font-mono"
                />
              )}
            </Field>
            <Field
              label="Bundle-ID"
              hint="Nur nötig, wenn diese App eine andere ist als die Standard-App des Servers."
              error={fieldErrors['config.bundle_id']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={bundleId}
                  onChange={(e) => setBundleId(e.target.value)}
                  placeholder="de.meshalert.app"
                  className="font-mono"
                />
              )}
            </Field>
          </>
        )}

        {generalError ? (
          <p role="alert" className="text-[13px] text-critical">
            {generalError}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Wird angelegt' : 'Hinzufügen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
